import { getRequestFirmId } from "./firm-context";
import moment from "moment";
import * as urls from "./url_helper";
import { consumeUserActivitySignal } from "./session-activity";

const methods = ["POST", "PUT", "PATCH"];

const isSystemCatalogueMutation = (path, method) => {
  if (method === "GET") return false;
  const value = path.toLowerCase();
  return (
    value.startsWith("/standards/") ||
    value.startsWith("/checklist/") ||
    value === "/master/financialyear" ||
    value === "/master/risk" ||
    value === "/master/riskcategory" ||
    value === "/master/worktype" ||
    value === "/master/clienttype" ||
    value === "/master/industrytype" ||
    (value.startsWith("/master/") && value.includes("reporttype")) ||
    (value.startsWith("/reviewtemplate/") &&
      (value.includes("/publish") || value.endsWith("/updatetemplate"))) ||
    (value.startsWith("/reporttemplate/templates/") &&
      value.endsWith("/publish")) ||
    value.startsWith("/admin/lifecycle/checklist") ||
    value.startsWith("/admin/lifecycle/standard") ||
    value.startsWith("/admin/lifecycle/service-code") ||
    value.startsWith("/admin/lifecycle/mandatory-service-code") ||
    value.startsWith("/admin/lifecycle/review-template") ||
    value.startsWith("/admin/lifecycle/report-template") ||
    value.startsWith("/admin/lifecycle/work-type") ||
    value.startsWith("/admin/lifecycle/client-type") ||
    value.startsWith("/admin/lifecycle/industry-type") ||
    value.startsWith("/admin/lifecycle/financial-year") ||
    value.startsWith("/admin/lifecycle/risk")
  );
};

const notifyCatalogueMutation = (path, method, result) => {
  if (result?.status === true && isSystemCatalogueMutation(path, method))
    window.dispatchEvent(new CustomEvent("piers:system-catalogue-changed"));
  return result;
};

const parseResponse = async (response) => {
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent("piers:session-expired"));
    const error = new Error("Your session has expired. Please sign in again.");
    error.status = 401;
    throw error;
  }

  const responseText = await response.text();
  let result = {};
  if (responseText) {
    try {
      result = JSON.parse(responseText);
    } catch {
      const error = new Error(
        response.ok
          ? "The server returned an invalid response."
          : `Request failed (${response.status}).`
      );
      error.status = response.status;
      throw error;
    }
  }
  if (!response.ok) {
    const validationMessages = Object.values(result.errors || {}).flat();
    const error = new Error(
      validationMessages[0] ||
        result.message ||
        result.detail ||
        result.title ||
        `Request failed (${response.status}).`
    );
    error.status = response.status;
    error.errors = result.errors;
    error.response = result;
    throw error;
  }
  return result;
};

const getDefaultHeaders = async () => {
  const headers = new Headers();
  if (getRequestFirmId()) headers.append("X-PIERS-Firm-Id", String(getRequestFirmId()));
  if (consumeUserActivitySignal())
    headers.append("X-PIERS-User-Activity", "true");
  return headers;
};

export const downloadFile = async (_url, method = "GET", body = "") => {
  const url = urls.BASE_URL + _url;

  const _headers = await getDefaultHeaders();

  const params = {
    method,
    headers: _headers,
    credentials: "include",
  };

  const request = new Request(url, params);
  return fetch(request).then(parseResponse);
};

const request = async (_url, method = "GET", body = "", signal) => {
  const url = urls.BASE_URL + _url;

  var headers = await getDefaultHeaders();
  headers.append("Content-Type", "application/json");

  const params = {
    method,
    headers: headers,
    credentials: "include",
    signal,
  };

  if (methods.includes(method)) {
    Date.prototype.toJSON = function () {
      return moment(this).format();
    };
    params.body = typeof body != "string" ? JSON.stringify(body) : body;
  }

  const request = new Request(url, params);
  return await fetch(request)
    .then(parseResponse)
    .then((result) => notifyCatalogueMutation(_url, method, result));
};

export const postRawData = async (_url, method, body) => {
  const url = urls.BASE_URL + _url;

  const _headers = await getDefaultHeaders();

  const params = {
    method,
    headers: _headers,
    credentials: "include",
  };

  params.body = body;

  const request = new Request(url, params);
  return fetch(request)
    .then(parseResponse)
    .then((result) => notifyCatalogueMutation(_url, method, result));
};

export const getData = async (url) => await request(url, "GET");
export const postData = async (url, data, signal) =>
  request(url, "POST", data, signal);
export const putData = async (url, data) => request(url, "PUT", data);
export const patchData = async (url, data) => request(url, "PATCH", data);
export const deleteData = async (url) => request(url, "DELETE");

export default {
  get: getData,
  post: postData,
  put: putData,
  patch: patchData,
  delete: deleteData,
};

export const getBlobData = async (path) => {
  const response = await fetch(urls.BASE_URL + path, {
    credentials: "include",
    headers: await getDefaultHeaders(),
  });
  if (!response.ok) throw new Error("Unable to download the document.");
  return response.blob();
};
